import React, { useState } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { MessageSquare, BellOff, Bell, LogOut, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Avatar from '../Avatar';
import Button from '../Button';
import UserName from '../UserName';
import type { Conversation } from '../../types';
import { useAuthStore } from '../../store/authStore';
import supabase from '../../lib/supabase';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId?: string;
  onSelect: (id: string) => void;
  searchQuery?: string;
  onConversationRemoved?: () => void;
}

export default function ConversationList({ 
  conversations, 
  selectedId, 
  onSelect, 
  searchQuery = '',
  onConversationRemoved 
}: ConversationListProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [showParticipants, setShowParticipants] = useState<string | null>(null);

  // Check if conversation is unread
  const isConversationUnread = (conversation: any) => {
    if (!conversation.last_message_at || !user) return false;
    
    const lastMessageTime = new Date(conversation.last_message_at);
    
    if (conversation.is_group) {
      // For group chats, check conversation_participants last_read_at
      const participant = conversation.participants?.find((p: any) => p.user_id === user.id);
      if (!participant?.last_read_at) return true;
      
      const lastReadTime = new Date(participant.last_read_at);
      return lastMessageTime > lastReadTime;
    } else {
      // For direct messages, check conversation read timestamps
      const lastReadField = conversation.user1_id === user.id 
        ? 'last_read_by_user1' 
        : 'last_read_by_user2';
      
      const lastReadTime = conversation[lastReadField];
      if (!lastReadTime) return true;
      
      return lastMessageTime > new Date(lastReadTime);
    }
  };

  const isFromCurrentUser = (conversation: any) => {
    return conversation.last_message_sender_id === user?.id;
  };

  // Filter conversations based on search query
  const filteredConversations = conversations.filter(conversation => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    
    if (conversation.is_group) {
      // Search in group name
      return conversation.name?.toLowerCase().includes(searchLower) ||
             conversation.last_message?.toLowerCase().includes(searchLower);
    } else {
      // Get the other user in direct conversations
      const otherUser = conversation.user1_id === user?.id ? conversation.user2 : conversation.user1;
      
      // Search in user name or last message
      return otherUser?.display_name?.toLowerCase().includes(searchLower) ||
             otherUser?.full_name.toLowerCase().includes(searchLower) ||
             conversation.last_message?.toLowerCase().includes(searchLower);
    }
  });

  const toggleMute = async (e: React.MouseEvent, conversationId: string, isMuted: boolean) => {
    e.stopPropagation(); // Prevent conversation selection

    try {
      const { error } = await supabase
        .from('conversation_participants')
        .update({ is_muted: !isMuted })
        .eq('conversation_id', conversationId)
        .eq('user_id', user?.id);

      if (error) throw error;

      // Refresh the conversations list
      if (onConversationRemoved) {
        onConversationRemoved();
      }
    } catch (err) {
      console.error('Error toggling mute:', err);
    }
  };

  const leaveGroup = async (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation(); // Prevent conversation selection

    try {
      const { error } = await supabase
        .from('conversation_participants')
        .delete()
        .eq('conversation_id', conversationId)
        .eq('user_id', user?.id);

      if (error) throw error;

      // Notify parent to refresh conversations
      if (onConversationRemoved) {
        onConversationRemoved();
      }
    } catch (err) {
      console.error('Error leaving group:', err);
    }
  };

  const handleProfileClick = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation(); // Prevent conversation selection
    navigate(`/users/${userId}`);
  };

  if (filteredConversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        {searchQuery ? (
          <>
            <MessageSquare className="w-12 h-12 text-neutral mb-4 opacity-50" />
            <h3 className="text-lg font-medium text-primary mb-2">No matches found</h3>
            <p className="text-neutral text-sm">
              Try different search terms
            </p>
          </>
        ) : (
          <>
            <MessageSquare className="w-12 h-12 text-neutral mb-4" />
            <h3 className="text-lg font-medium text-primary mb-2">No conversations yet</h3>
            <p className="text-neutral text-sm">
              Start connecting with other music professionals
            </p>
          </>
        )}
      </div>
    );
  }

  const formatMessageDate = (dateString?: string) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    if (isToday(date)) {
      return format(date, 'p'); // e.g. "3:30 PM"
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else if (date.getFullYear() === new Date().getFullYear()) {
      return format(date, 'MMM d'); // e.g. "Jan 5"
    } else {
      return format(date, 'MM/dd/yy'); // e.g. "01/05/22"
    }
  };

  return (
    <div className="divide-y divide-neutral divide-opacity-10">
      {filteredConversations.map((conversation) => {
        const isSelected = selectedId === conversation.id;
        const isHovered = hoveredId === conversation.id;
        const showingParticipants = showParticipants === conversation.id;
        const isUnread = isConversationUnread(conversation);
        const isOwnMessage = isFromCurrentUser(conversation);
        
        if (conversation.is_group) {
          // Group chat display
          const participant = conversation.participants?.find(p => p.user_id === user?.id);
          const isMuted = participant?.is_muted;
          
          return (
            <div
              key={conversation.id}
              className="relative"
              onMouseEnter={() => setHoveredId(conversation.id)}
              onMouseLeave={() => {
                setHoveredId(null);
                if (!isSelected) setShowParticipants(null);
              }}
            >
              <button
                onClick={() => onSelect(conversation.id)}
                className={`w-full p-4 flex items-center space-x-3 hover:bg-secondary  transition-colors ${
                  isSelected ? 'bg-secondary' : ''
                }`}
              >
                <Avatar
                  src={conversation.avatar_url}
                  alt={conversation.name || 'Group Chat'}
                  size="md"
                />
                
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className={`truncate text-primary ${
                        isUnread && !isOwnMessage ? 'font-bold' : 'font-medium'
                      }`}>
                        {conversation.name || 'Group Chat'}
                      </h3>
                      {/* Small unread indicator dot */}
                      {isUnread && !isOwnMessage && (
                        <div className="w-1.5 h-1.5 bg-accent rounded-full flex-shrink-0"></div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowParticipants(showingParticipants ? null : conversation.id);
                        }}
                        className="p-1 hover:bg-neutral rounded-full transition-colors"
                      >
                        <Users className="w-4 h-4 text-neutral hover:text-surface-light" />
                      </button>
                    </div>
                    {conversation.last_message_at && (
                      <span className="text-xs text-neutral">
                        {formatMessageDate(conversation.last_message_at)}
                      </span>
                    )}
                  </div>
                  
                  {conversation.last_message && (
                    <div className="flex items-center space-x-1">
                      {isOwnMessage && (
                        <span className="text-xs text-neutral">You:</span>
                      )}
                      <p className={`text-sm truncate text-neutral ${
                        isUnread && !isOwnMessage ? 'font-bold' : ''
                      }`}>
                        {conversation.last_message}
                      </p>
                    </div>
                  )}
                </div>
              </button>

              {/* Hover actions */}
              {isHovered && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    onClick={(e) => toggleMute(e, conversation.id, !!isMuted)}
                    className="p-1 hover:bg-neutral rounded-full transition-colors mt-6"
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? (
                      <BellOff className="w-4 h-4 text-neutral hover:text-surface-light" />
                    ) : (
                      <Bell className="w-4 h-4 text-neutral hover:text-surface-light" />
                    )}
                  </button>
                  <button
                    onClick={(e) => leaveGroup(e, conversation.id)}
                    className="p-1 hover:bg-neutral rounded-full transition-colors mt-6"
                    title="Leave group"
                  >
                    <LogOut className="w-4 h-4 text-neutral hover:text-surface-light" />
                  </button>
                </div>
              )}

              {/* Participants list */}
              {showingParticipants && (
                <div className="absolute right-5 top-full mt-0 w-44 bg-neutral shadow-elevation-2 rounded-lg z-20 p-2">                  
                  <div className="max-h-48 overflow-y-auto">
                    {conversation.participants?.map((participant) => (
                      <button
                        key={participant.user.id}
                        className="w-full flex items-center gap-2 p-2 hover:bg-secondary-light rounded-lg text-sm"
                        onClick={(e) => handleProfileClick(e, participant.user.id)}
                      >
                        <Avatar
                          src={participant.user.avatar_url}
                          alt={participant.user.display_name || participant.user.full_name}
                          size="sm"
                        />
                        <UserName
                          name={participant.user.display_name || participant.user.full_name}
                          verified={participant.user.verified}
                          size="sm"
                          userId={participant.user.id}
                          className="text-surface-light hover:text-primary"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        } else {
          // Direct message display
          const otherUser = conversation.user1_id === user?.id ? conversation.user2 : conversation.user1;
          const isUnread = isConversationUnread(conversation);
          const isOwnMessage = isFromCurrentUser(conversation);
          
          return (
            <button
              key={conversation.id}
              onClick={() => onSelect(conversation.id)}
              className={`w-full p-4 flex items-center space-x-3 hover:bg-secondary hover:text-surface-light transition-colors ${
                isSelected ? 'bg-secondary text-surface-light' : ''
              }`}
            >
              <div onClick={(e) => handleProfileClick(e, otherUser?.id)}>
                <Avatar
                  src={otherUser?.avatar_url}
                  alt={otherUser?.display_name || otherUser?.full_name || ''}
                  size="md"
                  className="cursor-pointer"
                />
              </div>
              
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserName
                      name={otherUser?.display_name || otherUser?.full_name}
                      verified={otherUser?.verified}
                      userId={otherUser?.id}
                      disableLink={true}
                      className={`text-primary ${
                        isUnread && !isOwnMessage ? 'font-bold' : ''
                      }`}
                    />
                    {/* Small unread indicator dot */}
                    {isUnread && !isOwnMessage && (
                      <div className="w-1.5 h-1.5 bg-accent rounded-full flex-shrink-0"></div>
                    )}
                  </div>
                  {conversation.last_message_at && (
                    <span className="text-xs text-neutral">
                      {formatMessageDate(conversation.last_message_at)}
                    </span>
                  )}
                </div>
                
                {conversation.last_message && (
                  <div className="flex items-center space-x-1">
                    {isOwnMessage && (
                      <span className="text-xs text-neutral">You:</span>
                    )}
                    <p className={`text-sm truncate text-neutral ${
                      isUnread && !isOwnMessage ? 'font-bold' : ''
                    }`}>
                      {conversation.last_message}
                    </p>
                  </div>
                )}
              </div>
            </button>
          );
        }
      })}
    </div>
  );
}